import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser, CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { format } from 'date-fns';
import { NgxChartsModule, Color, ScaleType, LegendPosition } from '@swimlane/ngx-charts';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { AnalyticsService } from '../../../../services/analytics.service';
import { AnalyticsFilters, ChartData, CategoryBreakdown, PositionBreakdown, LocationBreakdown } from '../../../../models/analytics.models';
import {
  AVAILABLE_CANDIDATES_COLOR_SCHEME,
  CATEGORY_BREAKDOWN_COLOR_SCHEME,
  POSITION_BREAKDOWN_COLOR_SCHEME,
  LOCATION_BREAKDOWN_COLOR_SCHEME,
} from '../../analytics-colors';

/**
 * Available Candidates Chart Component
 * Displays comprehensive dashboard with time series and breakdown charts
 * SSR-safe: Only renders charts in browser context
 */
@Component({
  selector: 'app-available-candidates-chart',
  standalone: true,
  imports: [CommonModule, NgxChartsModule, MatProgressSpinnerModule, MatCardModule, TranslocoModule],
  templateUrl: './available-candidates-chart.component.html',
  styleUrls: ['./available-candidates-chart.component.scss'],
})
export class AvailableCandidatesChartComponent implements OnInit, OnDestroy, OnChanges {
  private destroy$ = new Subject<void>();
  private readonly platformId = inject(PLATFORM_ID);
  readonly isBrowser = isPlatformBrowser(this.platformId);

  @Input() filters!: AnalyticsFilters;
  @Input() legendPosition: LegendPosition = LegendPosition.Right;

  // Raw API data storage for re-transformation on language change
  private rawData: any = null;

  // Chart data
  timeSeriesData: ChartData[] = [];
  categoryData: Array<{ name: string; value: number }> = [];
  positionData: Array<{ name: string; value: number }> = [];
  locationData: Array<{ name: string; value: number }> = [];

  // Time series chart configuration
  showXAxis = true;
  showYAxis = true;
  gradient = false;
  showLegend = true;
  showXAxisLabel = true;
  showYAxisLabel = true;
  animations = true;

  // Breakdown charts configuration
  showXAxisBar = true;
  showYAxisBar = true;
  showLegendBar = false;
  showXAxisLabelBar = false;
  showYAxisLabelBar = true;

  // Color schemes - different colors for candidates
  // Color schemes - using shared constants
  timeSeriesColorScheme: Color = AVAILABLE_CANDIDATES_COLOR_SCHEME;
  categoryColorScheme: Color = CATEGORY_BREAKDOWN_COLOR_SCHEME;
  positionColorScheme: Color = POSITION_BREAKDOWN_COLOR_SCHEME;
  locationColorScheme: Color = LOCATION_BREAKDOWN_COLOR_SCHEME;

  loading = false;
  error: string | null = null;

  constructor(private analyticsService: AnalyticsService, private translocoService: TranslocoService) {}

  ngOnInit(): void {
    // Only load chart data on browser
    if (this.isBrowser && this.filters) {
      this.loadChartData();
    }

    // Subscribe to language changes and re-transform data
    this.translocoService.langChanges$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.rawData) {
          this.transformAllData();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reload data when filters change
    if (changes['filters'] && !changes['filters'].firstChange && this.isBrowser) {
      this.loadChartData();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load chart data from API
   */
  private loadChartData(): void {
    if (!this.filters) {
      return;
    }

    this.loading = true;
    this.error = null;

    this.analyticsService
      .getAvailableCandidates(this.filters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          // Store raw data for language change re-transformation
          this.rawData = data;
          // Transform all chart data with current language
          this.transformAllData();
          this.loading = false;
        },
        error: (err) => {
          console.error('Chart data error:', err);
          this.error = this.translocoService.translate('analytics.errorLoadChartData');
          this.loading = false;
        },
      });
  }

  /**
   * Transform all chart data with current language
   * Called on initial load and when language changes
   */
  private transformAllData(): void {
    if (!this.rawData) {
      return;
    }

    this.timeSeriesData = this.transformTimeSeriesData(this.rawData.timeSeries);
    this.categoryData = this.transformCategoryData(this.rawData.breakdown.byCategory);
    this.positionData = this.transformPositionData(this.rawData.breakdown.byPosition);
    this.locationData = this.transformLocationData(this.rawData.breakdown.byLocation);
  }

  /**
   * Transform time series data to ngx-charts line chart format
   * Format: [{ name: 'Available Candidates', series: [{ name: 'Jan 01', value: 145 }] }]
   */
  private transformTimeSeriesData(timeSeries: Array<{ date: string; count: number }>): ChartData[] {
    if (!timeSeries || timeSeries.length === 0) {
      return [];
    }

    return [
      {
        name: this.translocoService.translate('analytics.availableCandidates'),
        series: timeSeries.map((item) => ({
          name: format(new Date(item.date), 'MMM dd'),
          value: item.count,
        })),
      },
    ];
  }

  /**
   * Transform category breakdown data to ngx-charts bar chart format
   * Backend returns top 9 + "Others", already sorted
   * categoryName is actually the categoryKey (e.g., "top_management")
   * Format: [{ name: 'Translated Category Name', value: 123 }]
   */
  private transformCategoryData(categories: CategoryBreakdown[]): Array<{ name: string; value: number }> {
    if (!categories || categories.length === 0) {
      return [];
    }

    return categories.map((item) => ({
      name: this.translocoService.translate(`jobCategories.${item.categoryName}`),
      value: item.count,
    }));
  }

  /**
   * Transform position breakdown data to ngx-charts bar chart format
   * Backend returns top 9 + "Others", already sorted
   * positionName is actually the positionKey (e.g., "manager")
   * Format: [{ name: 'Translated Position Name', value: 123 }]
   */
  private transformPositionData(positions: PositionBreakdown[]): Array<{ name: string; value: number }> {
    if (!positions || positions.length === 0) {
      return [];
    }

    return positions.map((item) => ({
      name: this.translocoService.translate(`jobPositions.${item.positionName}`),
      value: item.count,
    }));
  }

  /**
   * Transform location breakdown data to ngx-charts bar chart format
   * Backend returns top 9 + "Others", already sorted
   * Format: [{ name: 'City, Country', value: 123 }]
   */
  private transformLocationData(locations: LocationBreakdown[]): Array<{ name: string; value: number }> {
    if (!locations || locations.length === 0) {
      return [];
    }

    return locations.map((item) => {
      const name = item.country ? `${item.city}, ${item.country}` : item.city;
      return {
        name,
        value: item.count,
      };
    });
  }

  /**
   * Format Y-axis to show only whole numbers
   */
  yAxisTickFormatting(value: number): string {
    return Math.floor(value) === value ? value.toString() : '';
  }

  /**
   * Handle chart select event (optional)
   */
  onSelect(event: any): void {
    console.log('Chart item selected:', event);
  }
}
